<script setup lang="ts">
import {
  BoxComponent,
  ButtonComponent,
  DateRangeSelector,
  FlexComponent,
  formatNumber,
  formatNumberValue,
  getSocialNetwork,
  ImageComponent,
  InputComponent,
  LoadingComponent,
  PaginationComponent,
  type Segment,
  SegmentedFilter,
  SegmentSelector,
  type SegmentSelectorFilter,
  SocialItemEnum,
  TagSelector,
  TooltipComponent,
  TRAFFIC_SOURCE_FILTER_ITEMS,
  TrafficSourceCodeEnum,
  TypographyComponent,
} from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'
import { ElIcon } from 'element-plus'
import debounce from 'lodash/debounce'
import { computed, type Ref, ref, watch } from 'vue'

import { buildCreatorsExportUrl, useGetCreators } from '@/api/creators.api'
import { useFileDownload } from '@/api/file-download.api'
import { useSearchSegments } from '@/api/segments.api'
import { useGetUserTags } from '@/api/user-tags.api'
import ChevronIcon from '@/assets/icons/chevron.svg?component'
import ExportIcon from '@/assets/icons/export.svg?component'
import SearchIcon from '@/assets/icons/Magnifier.svg?component'
import UserDefaultIcon from '@/assets/icons/user-default.svg?component'
import { formatMoney } from '@/legacy/store/helpFunctions'
import { FLAG_SEGMENTS } from '@/lib/constants/flags'
import { useAdvertiserPermissions } from '@/lib/hooks'
import type { CreatorItem, CreatorItemFilter, CreatorItemSort, CreatorItemSortOrder } from '@/lib/types/creator.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'

const advertiserStore = useAdvertiserStore()
const advertiserId = computed<number | undefined>(() => advertiserStore.advertiser?.id)
const { hideClicks, hideCosts, hideSales, hideMedia, hideUsers, hideEdit } = useAdvertiserPermissions()
const isSegmentsEnabled = useFlag(FLAG_SEGMENTS)
const canFilterBySegment = computed(() => isSegmentsEnabled.value && !hideEdit.value)
const canViewCreator = computed(() => !(hideUsers.value || hideSales.value || hideCosts.value))
const NUMERIC_TRACK = 'minmax(80px, 1fr)'
const gridTemplate = computed(() => {
  const tracks = ['minmax(220px, 2fr)', NUMERIC_TRACK]
  if (!hideClicks.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideCosts.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideSales.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideMedia.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!(hideSales.value || hideCosts.value)) {
    tracks.push(NUMERIC_TRACK)
  }
  return tracks.join(' ')
})

const SOURCE_PLATFORM: Partial<Record<TrafficSourceCodeEnum, SocialItemEnum>> = {
  [TrafficSourceCodeEnum.Instagram]: SocialItemEnum.Instagram,
  [TrafficSourceCodeEnum.Tiktok]: SocialItemEnum.TikTok,
  [TrafficSourceCodeEnum.Snapchat]: SocialItemEnum.Snapchat,
}
const NAMED_SOURCE_PLATFORMS = Object.values(SOURCE_PLATFORM)

const filterStore = useGlobalFilterStore()
const page = ref(1)
const sortBy = ref<CreatorItemSort>('clicks')
const sortOrder = ref<CreatorItemSortOrder>('desc')
const searchQuery = ref<string | undefined>(undefined)
const tags = ref<number[]>([])
const segment = ref<Segment | undefined>()
const filter = computed<CreatorItemFilter>(() => ({
  page: page.value,
  sortBy: sortBy.value,
  sortOrder: sortOrder.value,
  dateFrom: filterStore.dateRange?.startDate,
  dateTo: filterStore.dateRange?.endDate,
  query: searchQuery.value,
  trafficSource: filterStore.trafficSource,
  tags: tags.value.length ? tags.value.map(String) : undefined,
  segmentId: segment.value?.id,
}))
watch(
  () => [
    sortBy.value,
    sortOrder.value,
    searchQuery.value,
    tags.value,
    segment.value,
    filterStore.trafficSource,
    filterStore.dateRange?.startDate,
    filterStore.dateRange?.endDate,
  ],
  () => {
    page.value = 1
  },
)

const { data: creators, totalPages, isLoading } = useGetCreators(advertiserId, filter)
const { data: tagOptions, isLoading: isTagsLoading } = useGetUserTags(advertiserId)
const { downloadFile, isLoading: isExporting } = useFileDownload()
const loadSegments = (segmentFilter: Ref<SegmentSelectorFilter>) => useSearchSegments(computed(() => segmentFilter.value.query ?? ''))
const searchInput = ref(searchQuery.value ?? '')

const applySearch = debounce((value: string) => {
  searchQuery.value = value || undefined
}, 500)

const handleSearchInput = (value: string) => {
  searchInput.value = value
  applySearch(value)
}

const handleSort = (column: CreatorItemSort) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'desc'
  }
}

const handleExport = async () => {
  if (!advertiserId.value) {
    return
  }
  await downloadFile(buildCreatorsExportUrl(advertiserId.value, filter.value), 'creator-stats.xlsx')
}

const sortIndicator = (column: CreatorItemSort): '' | 'asc' | 'desc' => (filter.value.sortBy === column ? filter.value.sortOrder : '')

const followersColumnLabel = computed(() => {
  const item = TRAFFIC_SOURCE_FILTER_ITEMS.find((source) => source.value === filter.value.trafficSource)
  return item ? `${item.label} followers` : 'Total followers'
})

const otherFollowers = (creator: CreatorItem): number =>
  Object.entries(creator.followersByPlatform).reduce(
    (sum, [type, value]) => (NAMED_SOURCE_PLATFORMS.includes(type as SocialItemEnum) ? sum : sum + value),
    0,
  )

const followersValue = (creator: CreatorItem): number => {
  const source = filter.value.trafficSource
  if (!source) {
    return creator.totalFollowers
  }
  if (source === TrafficSourceCodeEnum.Other) {
    return otherFollowers(creator)
  }
  const platform = SOURCE_PLATFORM[source]
  return platform ? (creator.followersByPlatform[platform] ?? 0) : 0
}

const followersBreakdown = (creator: CreatorItem) => {
  const entries = Object.entries(creator.followersByPlatform).map(([type, value]) => ({
    label: getSocialNetwork(type as SocialItemEnum)?.title ?? type,
    value,
    platform: type as SocialItemEnum,
  }))
  const source = filter.value.trafficSource
  if (!source) {
    return entries
  }
  if (source === TrafficSourceCodeEnum.Other) {
    return entries.filter((entry) => !NAMED_SOURCE_PLATFORMS.includes(entry.platform))
  }
  const platform = SOURCE_PLATFORM[source]
  return platform ? entries.filter((entry) => entry.platform === platform) : []
}

const showFollowersTooltip = (creator: CreatorItem) => followersBreakdown(creator).length > 0

const formatFollowers = (value: number) => formatNumber(value, 'compact')
const formatFollowersFull = (value: number) => formatNumber(value)
const isValueTruncated = (value: number) => formatFollowers(value) !== formatFollowersFull(value)
const formatRoas = (roas: number | null) => (roas === null ? '-' : formatNumberValue(roas, 'standard', 'decimal', 2, 'en-GB', 2))
</script>
<template>
  <BoxComponent>
    <BoxComponent :mb="24">
      <FlexComponent justify="space-between" align="center" :gap="16">
        <SegmentedFilter
          :model-value="filterStore.trafficSource"
          :items="TRAFFIC_SOURCE_FILTER_ITEMS"
          :max="4"
          aria-label="Traffic source"
          @update:model-value="filterStore.setTrafficSource"
        />
        <FlexComponent :gap="12" align="center">
          <BoxComponent :w="150" class="creators-list__search">
            <InputComponent :model-value="searchInput" placeholder="Search creators" size="large" @input="handleSearchInput">
              <template #prefix>
                <ElIcon :size="16">
                  <SearchIcon />
                </ElIcon>
              </template>
            </InputComponent>
          </BoxComponent>
          <SegmentSelector
            v-if="canFilterBySegment"
            v-model:segment="segment"
            :advertiser-id="advertiserId"
            :load-segments="loadSegments"
            should-truncate-text
          />
          <TagSelector v-model="tags" :options="tagOptions" :loading="isTagsLoading" />
          <DateRangeSelector :date-range="filterStore.dateRange" borderless @update:date-range="filterStore.setDateRange" />
          <ButtonComponent variant="accent" :is-loading="isExporting" class="creators-list__export" @click="handleExport">
            <template #iconLeft>
              <ElIcon :size="16">
                <ExportIcon />
              </ElIcon>
            </template>
            Export
          </ButtonComponent>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
    <BoxComponent class="creators-list__table" :style="{ gridTemplateColumns: gridTemplate }">
      <div class="creators-list__heading" role="row">
        <TypographyComponent variant="label-medium" class="creators-list__col--creator">Creator</TypographyComponent>
        <button class="creators-list__th creators-list__col--followers" @click="handleSort('total_followers')">
          <TypographyComponent variant="label-medium">{{ followersColumnLabel }}</TypographyComponent>
          <ElIcon
            v-if="sortIndicator('total_followers')"
            :size="18"
            class="creators-list__sort"
            :data-direction="sortIndicator('total_followers')"
          >
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideClicks" class="creators-list__th" @click="handleSort('clicks')">
          <TypographyComponent variant="label-medium">Clicks</TypographyComponent>
          <ElIcon v-if="sortIndicator('clicks')" :size="18" class="creators-list__sort" :data-direction="sortIndicator('clicks')">
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideCosts" class="creators-list__th" @click="handleSort('store_cost')">
          <TypographyComponent variant="label-medium">Cost</TypographyComponent>
          <ElIcon v-if="sortIndicator('store_cost')" :size="18" class="creators-list__sort" :data-direction="sortIndicator('store_cost')">
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideSales" class="creators-list__th" @click="handleSort('order_value')">
          <TypographyComponent variant="label-medium">Order value</TypographyComponent>
          <ElIcon v-if="sortIndicator('order_value')" :size="18" class="creators-list__sort" :data-direction="sortIndicator('order_value')">
            <ChevronIcon />
          </ElIcon>
        </button>
        <TypographyComponent v-if="!hideMedia" variant="label-medium" class="creators-list__col--numeric">
          Est. media value
        </TypographyComponent>
        <button v-if="!(hideSales || hideCosts)" class="creators-list__th" @click="handleSort('roas')">
          <TypographyComponent variant="label-medium">ROAS</TypographyComponent>
          <ElIcon v-if="sortIndicator('roas')" :size="18" class="creators-list__sort" :data-direction="sortIndicator('roas')">
            <ChevronIcon />
          </ElIcon>
        </button>
      </div>
      <BoxComponent v-if="isLoading" class="creators-list__feedback">
        <LoadingComponent />
      </BoxComponent>
      <BoxComponent v-else-if="creators.length === 0" class="creators-list__feedback">
        <TypographyComponent variant="title-medium">No creators found</TypographyComponent>
      </BoxComponent>
      <template v-else>
        <div v-for="creator in creators" :key="creator.userId" class="creators-list__row" role="row">
          <FlexComponent :gap="12" align="center" class="creators-list__col--creator">
            <ImageComponent
              v-if="creator.avatar"
              :image="creator.avatar"
              :width="56"
              :height="56"
              :alt="creator.username"
              class="creators-list__avatar"
            />
            <FlexComponent v-else align="center" justify="center" class="creators-list__avatar creators-list__avatar--empty">
              <ElIcon :size="20"><UserDefaultIcon /></ElIcon>
            </FlexComponent>
            <FlexComponent direction="column" :gap="4" class="creators-list__creator-meta">
              <RouterLink v-if="canViewCreator" :to="{ path: `/creators/${creator.userId}` }" class="creators-list__name">
                <TypographyComponent variant="title-large" text-truncate>{{ creator.username }}</TypographyComponent>
              </RouterLink>
              <TypographyComponent v-else variant="title-large" text-truncate class="creators-list__name--static">
                {{ creator.username }}
              </TypographyComponent>
              <FlexComponent v-if="creator.tags.length" :gap="4" wrap="wrap">
                <BoxComponent v-for="tag in creator.tags" :key="tag.id" class="creators-list__tag">
                  <TypographyComponent variant="label-small">{{ tag.name }}</TypographyComponent>
                </BoxComponent>
              </FlexComponent>
            </FlexComponent>
          </FlexComponent>
          <div class="creators-list__col--numeric">
            <TooltipComponent v-if="showFollowersTooltip(creator)" placement="top" :raw-content="false">
              <TypographyComponent variant="body-medium" class="creators-list__followers">
                {{ formatFollowers(followersValue(creator)) }}
              </TypographyComponent>
              <template #content>
                <FlexComponent direction="column" :gap="4">
                  <TypographyComponent v-if="!filter.trafficSource" variant="label-medium">Total followers</TypographyComponent>
                  <TypographyComponent v-for="entry in followersBreakdown(creator)" :key="entry.label" variant="body-medium">
                    {{ entry.label }}: {{ formatFollowersFull(entry.value) }}
                  </TypographyComponent>
                </FlexComponent>
              </template>
            </TooltipComponent>
            <TypographyComponent v-else variant="body-medium">{{ formatFollowers(followersValue(creator)) }}</TypographyComponent>
          </div>
          <div v-if="!hideClicks" class="creators-list__col--numeric">
            <TooltipComponent ref="clicksTooltip" placement="top" :disabled="!isValueTruncated(creator.clicks)">
              <TypographyComponent variant="body-medium" :class="{ 'creators-list__clicks': isValueTruncated(creator.clicks) }">
                {{ formatFollowers(creator.clicks) }}
              </TypographyComponent>
              <template #content>
                <TypographyComponent variant="body-medium">{{ formatFollowersFull(creator.clicks) }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </div>
          <TypographyComponent v-if="!hideCosts" variant="body-medium" class="creators-list__col--numeric">
            {{ formatMoney(creator.cost) }}
          </TypographyComponent>
          <TypographyComponent v-if="!hideSales" variant="body-medium" class="creators-list__col--numeric">
            {{ formatMoney(creator.orderValue) }}
          </TypographyComponent>
          <TypographyComponent v-if="!hideMedia" variant="body-medium" class="creators-list__col--numeric">
            {{ formatMoney(creator.mediaValue.amount) }}
          </TypographyComponent>
          <TypographyComponent v-if="!(hideSales || hideCosts)" variant="body-medium" class="creators-list__col--numeric">
            {{ formatRoas(creator.roas) }}
          </TypographyComponent>
        </div>
      </template>
    </BoxComponent>
    <BoxComponent v-if="totalPages > 1" :mt="24">
      <PaginationComponent v-model:current-page="page" :total-pages="totalPages" />
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.creators-list__search {
  :deep(.el-input__wrapper) {
    box-shadow: none;
    background: transparent;
    padding-inline: 0;
    border-bottom: 1px solid transparent;
    border-radius: 0;
    transition: border-color var(--transition) ease;
    &:hover {
      box-shadow: none;
    }
  }
  &:focus-within :deep(.el-input__wrapper) {
    border-bottom-color: var(--black, #000);
  }
}
.creators-list__export.creators-list__export {
  :deep(svg) {
    fill: currentColor;
  }
}
.creators-list__table {
  display: grid;
  row-gap: 8px;
}
.creators-list__heading,
.creators-list__row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  column-gap: 16px;
  align-items: center;
  padding: 24px;
}
.creators-list__heading {
  padding-block: 8px;
  :deep(.label-medium) {
    font-size: 12px;
    line-height: 18px;
  }
}
.creators-list__row {
  background: var(--inverted);
  border: 1px solid var(--secondary-8);
  border-radius: 16px;
}
.creators-list__row:hover {
  background: #fdfdfd;
}

.creators-list__th {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: right;
}
.creators-list__sort {
  transition: transform var(--transition) ease;
  &[data-direction='desc'] {
    transform: rotate(180deg);
  }
}
.creators-list__col--numeric {
  text-align: right;
}
.creators-list__col--creator {
  min-width: 0;
  overflow: hidden;
}
.creators-list__creator-meta {
  min-width: 0;
}
.creators-list__avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
}
.creators-list__avatar--empty {
  background: var(--surface);
  color: var(--secondary-48);
}
.creators-list__name {
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  &:hover {
    text-decoration: underline;
  }
}
.creators-list__name--static {
  color: inherit;
  overflow: hidden;
}
.creators-list__tag {
  padding: 2px 4px;
  background: var(--secondary-12);
  border-radius: 4px;
  color: var(--primary);
}
.creators-list__followers,
.creators-list__clicks {
  cursor: default;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  width: fit-content;
  margin-left: auto;
}
.creators-list__feedback {
  grid-column: 1 / -1;
  padding: 48px;
  text-align: center;
}
</style>
